/* eslint-disable no-console */
 
import { useState, useEffect, useRef, useMemo } from 'react';
import { useOptimizedSubmitReferentsForm } from '@/hooks/useOptimizedSubmitReferentsForm';
import { useReferentBranding } from '@/hooks/useReferentBranding';
import { useDeviceFingerprint } from '@/hooks/useDeviceFingerprint';
import { relationTypes, type RelationType } from '@/types/referents';
import { FingerprintConsentCheckbox } from '@/components/forms/FingerprintConsentCheckbox';
import { useReferentBrandingColors } from '@/hooks/useReferentBrandingColors';
import { BrandedBackground, BrandedText, BrandedIcon } from '@/components/shared/ui';

// import { validateAndFormatPhone } from '@/utils/phoneValidation'; // DÉSACTIVÉ - SMS désactivé
import { Loader2, AlertTriangle } from 'lucide-react'; // Check, X retirés - SMS désactivé

interface OptimizedReferentsFormProps {
  campaign: any;
  onSubmit: (data: any) => Promise<void>;
  isSubmitting: boolean;
}

interface SubmitData {
  referents: any[];
  fingerprintConsent: boolean;
  deviceFingerprint?: string | null;
}

export const OptimizedReferentsForm = ({ campaign, onSubmit, isSubmitting }: OptimizedReferentsFormProps) => {
  const referentsCount = campaign.referents_requested_count;
  const { branding } = useReferentBranding(campaign?.id);
  const colors = useReferentBrandingColors(branding);

  // Helper pour vérifier et utiliser le branding
  const getBrandedValue = (value: string | undefined | null, defaultValue: string) => {
    return (branding?.enabled && value) ? value : defaultValue;
  };

  // Debug: Afficher les données reçues avec sources de référents
  console.log('🔍 OptimizedReferentsForm - Données reçues:', {
    campaign,
    ai_extracted_data: campaign.ai_extracted_data,
    recommendedReferents: campaign.ai_extracted_data?.recommendedReferents,
    selected_referents: campaign.selected_referents, // ← NOUVEAU: Choix du recruteur
    experiences: campaign.ai_extracted_data?.experiences
  });

  // Récupérer les référents sélectionnés - PRIORITÉ AU CHOIX DU RECRUTEUR
  const selectedReferents = useMemo(() => {
    const recruiterChoice = campaign.selected_referents;
    const aiRecommendations = campaign.ai_extracted_data?.recommendedReferents;
    
    // Logs pour tracer la source utilisée
    if (recruiterChoice && recruiterChoice.length > 0) {
      console.log('✅ Utilisation du choix RECRUTEUR:', recruiterChoice);
      return recruiterChoice;
    } else if (aiRecommendations && aiRecommendations.length > 0) {
      console.log('⚠️ Fallback vers recommandations IA:', aiRecommendations);
      return aiRecommendations;
    } else {
      console.log('❌ Aucun référent trouvé');
      return [];
    }
  }, [campaign.selected_referents, campaign.ai_extracted_data?.recommendedReferents]);

  console.log('📋 Référents sélectionnés:', selectedReferents);

  // Fonction pour récupérer le nom de l'entreprise du référent
  const getReferentCompany = useMemo(() => {
    return (index: number): string => {
      const experiences = campaign?.ai_extracted_data?.experiences;
      const selectedReferent = selectedReferents?.[index];
      
      // Debug en développement avec source des données
      if (process.env.NODE_ENV === 'development') {
        console.log(`🏢 getReferentCompany(${index}):`, {
          selectedReferent: selectedReferent,
          experienceId: selectedReferent?.experienceId,
          experiencesCount: experiences?.length,
          source: selectedReferents === campaign.selected_referents ? 'RECRUTEUR' : 'IA_RECOMMENDATIONS'
        });
      }
      
      if (!experiences || experiences.length === 0) {
        return 'Non spécifiée';
      }
      
      // Stratégie 1: Correspondance directe via experienceId
      if (selectedReferent?.experienceId) {
        const experience = experiences.find((exp: any) => exp.id === selectedReferent.experienceId);
        if (experience?.company) {
          console.log(`✅ Entreprise trouvée via experienceId: ${experience.company}`);
          return experience.company;
        }
      }
      
      // Stratégie 2: Correspondance via recommendedReferents
      if (campaign?.ai_extracted_data?.recommendedReferents && selectedReferent?.experienceRef) {
        const matchingRecommended = campaign.ai_extracted_data.recommendedReferents.find((rec: any) =>
          rec.experienceRef === selectedReferent.experienceRef
        );
        if (matchingRecommended?.experienceId) {
          const experience = experiences.find((exp: any) => exp.id === matchingRecommended.experienceId);
          if (experience?.company) {
            console.log(`✅ Entreprise trouvée via experienceRef: ${experience.company}`);
            return experience.company;
          }
        }
      }
      
      // Stratégie 3: Correspondance par index
      if (campaign?.ai_extracted_data?.recommendedReferents && campaign.ai_extracted_data.recommendedReferents[index]) {
        const recommendedByIndex = campaign.ai_extracted_data.recommendedReferents[index];
        if (recommendedByIndex?.experienceId) {
          const experience = experiences.find((exp: any) => exp.id === recommendedByIndex.experienceId);
          if (experience?.company) {
            console.log(`✅ Entreprise trouvée par index: ${experience.company}`);
            return experience.company;
          }
        }
      }
      
      // Stratégie 4: Fallback
      const fallbackCompany = experiences[0]?.company || 'Non spécifiée';
      console.log(`⚠️ Utilisation fallback: ${fallbackCompany}`);
      return fallbackCompany;
    };
  }, [campaign?.ai_extracted_data, campaign.selected_referents, selectedReferents]);

  const {
    form,
    validateForSubmission,
    isValidating,
    validationErrors
  } = useOptimizedSubmitReferentsForm(campaign.candidate_email, referentsCount || 1);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fingerprintConsent, setFingerprintConsent] = useState(false);

  // const [phoneValidationStatus, setPhoneValidationStatus] = useState<Record<number, { isValid: boolean; error?: string }>>({}); // DÉSACTIVÉ - SMS désactivé
  const hasInitialized = useRef(false);

  // Device fingerprinting avec consentement
  const { fingerprint, isGenerating, error: fingerprintError } = useDeviceFingerprint(fingerprintConsent);

  const handleSubmit = async (data: any) => {
    setSubmitError(null);

    // NOUVEAU: Ajouter l'experience_id pour chaque référent avant validation
    data.referents.forEach((referent: any, index: number) => {
      // Récupérer l'experience_id depuis selectedReferents
      if (selectedReferents && selectedReferents[index]) {
        const selectedReferent = selectedReferents[index];

        console.log(`🔗 Association référent ${index + 1}:`, {
          referentName: referent.name,
          selectedReferent: selectedReferent,
          experienceId: selectedReferent.experienceId,
          experienceRef: selectedReferent.experienceRef,
          source: selectedReferents === campaign.selected_referents ? 'RECRUTEUR' : 'IA_RECOMMENDATIONS'
        });

        // Stratégie 1: experienceId direct
        if (selectedReferent.experienceId) {
          referent.experience_id = selectedReferent.experienceId;
          console.log(`✅ Experience_id assigné via experienceId: ${referent.experience_id}`);
        }
        // Stratégie 2: via recommendedReferents
        else if (selectedReferent.experienceRef && campaign.ai_extracted_data?.recommendedReferents) {
          const matchingRecommended = campaign.ai_extracted_data.recommendedReferents.find((rec: any) =>
            rec.experienceRef === selectedReferent.experienceRef
          );
          if (matchingRecommended?.experienceId) {
            referent.experience_id = matchingRecommended.experienceId;
            console.log(`✅ Experience_id assigné via recommendedReferents: ${referent.experience_id}`);
          }
        }
        // Stratégie 3: par index (fallback)
        else if (campaign.ai_extracted_data?.recommendedReferents && campaign.ai_extracted_data.recommendedReferents[index]) {
          const recommendedByIndex = campaign.ai_extracted_data.recommendedReferents[index];
          if (recommendedByIndex?.experienceId) {
            referent.experience_id = recommendedByIndex.experienceId;
            console.log(`✅ Experience_id assigné par index: ${referent.experience_id}`);
          }
        }

        if (!referent.experience_id) {
          console.warn(`⚠️ Aucun experience_id trouvé pour le référent ${index + 1}: ${referent.name}`);
        }


      }
    });



    // Validation finale avant soumission
    const validation = await validateForSubmission(data);

    if (!validation.isValid) {
      console.error('Erreurs de validation:', validation.errors);
      setSubmitError('Veuillez corriger les erreurs de validation avant de soumettre');
      return;
    }

    try {
      // Préparer les données avec fingerprinting
      const submitData: SubmitData = {
        referents: data.referents,
        fingerprintConsent,
        deviceFingerprint: fingerprintConsent ? fingerprint : null
      };

      console.log('🔍 Soumission avec fingerprinting:', {
        referentsCount: submitData.referents.length,
        hasConsent: submitData.fingerprintConsent,
        hasFingerprint: !!submitData.deviceFingerprint,
        fingerprintLength: submitData.deviceFingerprint?.length
      });

      await onSubmit(submitData);
    } catch (_error) {
      console.error('Erreur lors de la soumission:', _error);
      setSubmitError('Une erreur est survenue lors de la soumission');
    }
  };

  const referents = form.watch('referents');

  const getRelationTypeLabel = (value: string) => {
    const type = relationTypes.find(t => t.value === value);
    return type ? type.label : value;
  };

  // DÉSACTIVÉ - SMS désactivé
  // La fonction handlePhoneBlur a été désactivée avec le système SMS
  // Pour réactiver : décommenter cette fonction et la déclaration de phoneValidationStatus
  /*
  const handlePhoneBlur = (phone: string, index: number) => {
    if (!phone || phone.trim() === '') {
      setPhoneValidationStatus(prev => {
        const { [index]: _removed, ...rest } = prev;
        return rest;
      });
      return;
    }

    const validation = validateAndFormatPhone(phone);
    if (validation.isValid && validation.formatted) {
      form.setValue(`referents.${index}.phone`, validation.formatted);
      setPhoneValidationStatus(prev => ({
        ...prev,
        [index]: { isValid: true }
      }));
    } else {
      setPhoneValidationStatus(prev => ({
        ...prev,
        [index]: { isValid: false, error: validation.error || 'Format invalide' }
      }));
    }
  };
  */





  // Fonction pour mapper les types de relation AI vers les types acceptés par le schéma
  const mapAIRelationTypeToSchema = (aiRelationType: string): RelationType => {
    const mappings: Record<string, RelationType> = {
      'manager direct': 'manager',
      'manager': 'manager',
      'superviseur': 'manager',
      'chef': 'manager',
      'directeur': 'manager',
      'responsable': 'manager',
      'collègue': 'collègue',
      'colleague': 'collègue',
      'équipier': 'collègue',
      'collaborateur': 'collègue',
      'client': 'client',
      'customer': 'client',
      'autre': 'autre',
      'other': 'autre',
    };

    // Normaliser la valeur en minuscules pour la comparaison
    const normalizedValue = aiRelationType?.toLowerCase().trim();

    // Retourner la valeur mappée ou 'manager' par défaut
    return mappings[normalizedValue] || 'manager';
  };

  const getReferentRelationType = (referentIndex: number): RelationType => {
    if (!selectedReferents || !selectedReferents[referentIndex]) return 'manager';

    const referent = selectedReferents[referentIndex];
    const aiRelationType = referent?.suggestedRole || referent?.relationType || 'manager';

    // Mapper la valeur AI vers une valeur acceptée par le schéma
    return mapAIRelationTypeToSchema(aiRelationType);
  };

  // Initialiser les champs avec les données AI si disponibles
  useEffect(() => {
    if (selectedReferents && selectedReferents.length > 0 && !hasInitialized.current) {

      selectedReferents.forEach((referent: any, index: number) => {
        if (index < referentsCount) {
          // Extraire le nom depuis name ou une autre propriété
          const referentName = referent.name || referent.suggestedName || '';
          form.setValue(`referents.${index}.name`, referentName);
          form.setValue(`referents.${index}.position`, referent.suggestedPosition || '');
          form.setValue(`referents.${index}.relation_type`, mapAIRelationTypeToSchema(referent.suggestedRole || referent.relationType || 'manager'));
        }
      });

      hasInitialized.current = true;
    }
  }, [selectedReferents, form, referentsCount]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4 font-['Bricolage_Grotesque']">
      <div className="w-full max-w-[646px] bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex flex-col justify-start items-start overflow-hidden">
        {/* Header avec logo et badge */}
        <div className="self-stretch px-4 sm:px-7 py-6 border-b border-neutral-400/20 flex flex-col sm:flex-row justify-between items-center gap-4">
          <img
            className="w-32 sm:w-40 h-auto"
            src={getBrandedValue(branding?.logoUrl, "/lovable-uploads/c941c7a1-d271-4c8b-91a4-2611677da8a6.png")}
            alt={getBrandedValue(branding?.logoUrl, '') ? "Logo Entreprise" : "Getproof Logo"}
          />
          <div
            className="px-2 py-1.5 rounded-md outline outline-1 outline-offset-[-1px] flex justify-center items-center gap-2.5"
            style={{
              outlineColor: getBrandedValue(branding?.primaryColor, '#2dd4bf'),
              color: getBrandedValue(branding?.primaryColor, '#2dd4bf')
            }}
          >
            <div className="justify-start text-xs sm:text-sm font-medium font-['Bricolage_Grotesque'] capitalize tracking-tight text-center">
              Prise de référence professionnelle
            </div>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="self-stretch px-4 sm:px-7 py-8 flex flex-col justify-end items-center gap-6">
          {/* Titre principal */}
          <div className="self-stretch flex flex-col justify-start items-start gap-2">
            <div className="text-center justify-start text-gray-800 text-xl sm:text-2xl font-medium font-['Bricolage_Grotesque'] leading-7">
              Référence(s) pour {campaign.candidate_name}
            </div>
            <div className="self-stretch justify-start text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque'] leading-none">
              Poste à pourvoir : {campaign.target_position}
            </div>
          </div>

          {/* Formulaire */}
          <form onSubmit={form.handleSubmit(handleSubmit)} className="self-stretch flex flex-col justify-start items-start gap-4">
            {referents.map((_, index) => {
              const requiredRelationType = getReferentRelationType(index);
              const referentCompany = getReferentCompany(index);

              return (
                <div key={index}>
                  <div className="w-full rounded-[10px] flex flex-col justify-start items-start gap-4">
                    {/* Titre de la référence */}
                    <div className="w-full flex flex-col justify-start items-start gap-1">
                      <div className="self-stretch justify-start text-gray-800 text-lg sm:text-xl font-medium font-['Bricolage_Grotesque'] leading-normal">
                        Référence {index + 1}
                      </div>
                      <div className="self-stretch justify-center text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque']">
                        Type de référence requis : {getRelationTypeLabel(requiredRelationType)} (défini par le recruteur)
                      </div>
                    </div>

                    {/* Champs du formulaire */}
                    <div className="w-full flex flex-col justify-start items-start gap-3">
                      {/* Première ligne : Nom et Email */}
                      <div className="self-stretch flex flex-col sm:flex-row justify-start items-start gap-3">
                        <div className="w-full sm:flex-1 inline-flex flex-col justify-start items-start gap-2">
                          <div className="self-stretch justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
                            Nom et prénom
                          </div>
                          <div className="self-stretch px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex justify-between items-center">
                            <input
                              {...form.register(`referents.${index}.name`)}
                              placeholder="Nom de la référence"
                              className="flex-1 justify-center text-gray-800 text-sm font-normal font-['Bricolage_Grotesque'] bg-transparent border-none outline-none"
                            />
                          </div>
                          {form.formState.errors.referents?.[index]?.name && (
                            <p className="text-sm text-red-600 mt-1">
                              {form.formState.errors.referents[index].name?.message}
                            </p>
                          )}
                        </div>
                        <div className="w-full sm:flex-1 inline-flex flex-col justify-start items-start gap-2">
                          <div className="self-stretch justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
                            E-mail professionnel
                          </div>
                          <div className="self-stretch px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex justify-between items-center">
                            <input
                              type="email"
                              {...form.register(`referents.${index}.email`)}
                              placeholder="email@company.com"
                              className="flex-1 justify-center text-gray-800 text-sm font-normal font-['Bricolage_Grotesque'] bg-transparent border-none outline-none"
                            />
                          </div>
                          {/* Erreurs de validation email basiques (Zod uniquement) */}
                          {form.formState.errors.referents?.[index]?.email && (
                            <div className="flex items-start gap-2 mt-1">
                              <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-red-600">
                                {form.formState.errors.referents[index].email?.message}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* CHAMP TÉLÉPHONE DÉSACTIVÉ - Pour réactiver, décommenter le bloc ci-dessous 
                      <div className="self-stretch flex flex-col justify-start items-start gap-2">
                        <div className="self-stretch justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
                          Numéro de téléphone (optionnel)
                        </div>
                        <div className="self-stretch px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex justify-between items-center gap-2">
                          <input
                            type="tel"
                            {...form.register(`referents.${index}.phone`)}
                            placeholder="+33612345678"
                            onBlur={(e) => handlePhoneBlur(e.target.value, index)}
                            className="flex-1 justify-center text-gray-800 text-sm font-normal font-['Bricolage_Grotesque'] bg-transparent border-none outline-none"
                          />
                          {phoneValidationStatus[index] && (
                            <div className="flex-shrink-0">
                              {phoneValidationStatus[index].isValid ? (
                                <Check className="h-4 w-4 text-green-500" />
                              ) : (
                                <X className="h-4 w-4 text-red-500" />
                              )}
                            </div>
                          )}
                        </div>
                        {phoneValidationStatus[index] && !phoneValidationStatus[index].isValid && (
                          <p className="text-sm text-red-600 mt-1">
                            {phoneValidationStatus[index].error}
                          </p>
                        )}
                        <div className="justify-center text-neutral-400 text-[10px] font-normal font-['Bricolage_Grotesque']">
                          Format: +33612345678 (pour recevoir un SMS de rappel)
                        </div>
                      </div>
                      */}

                      {/* Troisième ligne : Position et Entreprise */}
                      <div className="self-stretch flex flex-col sm:flex-row justify-start items-start gap-3">
                        <div className="w-full sm:flex-1 inline-flex flex-col justify-start items-start gap-2">
                          <div className="self-stretch justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
                            Position
                          </div>
                          <div className="self-stretch px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex justify-between items-center">
                            <input
                              {...form.register(`referents.${index}.position`)}
                              placeholder="Titre d'emploi"
                              className="flex-1 justify-center text-gray-800 text-sm font-normal font-['Bricolage_Grotesque'] bg-transparent border-none outline-none"
                            />
                          </div>
                          {form.formState.errors.referents?.[index]?.position && (
                            <p className="text-sm text-red-600 mt-1">
                              {form.formState.errors.referents[index].position?.message}
                            </p>
                          )}
                        </div>
                        <div className="w-full sm:flex-1 inline-flex flex-col justify-start items-start gap-2">
                          <div className="self-stretch justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
                            Entreprise
                          </div>
                          <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                            <div className="self-stretch px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex justify-between items-center">
                              <div className="flex-1 justify-center text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
                                {referentCompany}
                              </div>
                            </div>
                            <div className="justify-center text-neutral-400 text-[10px] font-normal font-['Bricolage_Grotesque']">
                              Entreprise avec laquelle vous avez collaboré avec la référence
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Quatrième ligne : Dates de collaboration */}
                      <div className="self-stretch flex flex-col justify-start items-start gap-3">
                        <div className="self-stretch justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
                          Dates de collaboration
                        </div>
                        
                        {/* Date de début */}
                        <div className="w-full flex flex-col justify-start items-start gap-2">
                          <div className="self-stretch justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
                            Date de début de collaboration
                          </div>
                          <div className="self-stretch px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex justify-between items-center">
                            <input
                              type="date"
                              {...form.register(`referents.${index}.candidate_collaboration_start_date`)}
                              className="flex-1 justify-center text-gray-800 text-sm font-normal font-['Bricolage_Grotesque'] bg-transparent border-none outline-none"
                              placeholder="Sélectionnez une date"
                            />
                          </div>
                          {form.formState.errors.referents?.[index]?.candidate_collaboration_start_date && (
                            <p className="text-sm text-red-600 mt-1">
                              {form.formState.errors.referents[index].candidate_collaboration_start_date?.message}
                            </p>
                          )}
                        </div>

                        {/* Checkbox "Nous travaillons toujours ensemble" */}
                        <div className="w-full flex items-center gap-3">
                          <input
                            type="checkbox"
                            {...form.register(`referents.${index}.candidate_still_working_together`)}
                            id={`still-working-${index}`}
                            className="w-4 h-4 bg-gray-100 border-gray-300 rounded focus:ring-2"
                            style={{
                              color: colors.primary,
                              '--tw-ring-color': colors.primary
                            } as React.CSSProperties}
                            onChange={(e) => {
                              // Si la checkbox est cochée, vider la date de fin
                              if (e.target.checked) {
                                form.setValue(`referents.${index}.candidate_collaboration_end_date`, '');
                              }
                            }}
                          />
                          <label 
                            htmlFor={`still-working-${index}`}
                            className="text-sm font-normal font-['Bricolage_Grotesque'] text-gray-700 cursor-pointer"
                          >
                            Nous travaillons toujours ensemble
                          </label>
                        </div>

                        {/* Date de fin (conditionnelle) */}
                        {!form.watch(`referents.${index}.candidate_still_working_together`) && (
                          <div className="w-full flex flex-col justify-start items-start gap-2">
                            <div className="self-stretch justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
                              Date de fin de collaboration (optionnelle)
                            </div>
                            <div className="self-stretch px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex justify-between items-center">
                              <input
                                type="date"
                                {...form.register(`referents.${index}.candidate_collaboration_end_date`)}
                                className="flex-1 justify-center text-gray-800 text-sm font-normal font-['Bricolage_Grotesque'] bg-transparent border-none outline-none"
                                placeholder="Sélectionnez une date"
                                min={form.watch(`referents.${index}.candidate_collaboration_start_date`)}
                              />
                            </div>
                            {form.formState.errors.referents?.[index]?.candidate_collaboration_end_date && (
                              <p className="text-sm text-red-600 mt-1">
                                {form.formState.errors.referents[index].candidate_collaboration_end_date?.message}
                              </p>
                            )}
                          </div>
                        )}
                        
                        <div className="justify-center text-neutral-400 text-[10px] font-normal font-['Bricolage_Grotesque']">
                          Ces dates aideront votre référent à mieux contextualiser votre collaboration
                        </div>
                      </div>

                      {/* Cinquième ligne : Type de relation */}
                      <div className="self-stretch flex flex-col justify-start items-start gap-2">
                        <div className="self-stretch justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
                          Type de relation professionnelle
                        </div>
                        <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                          <div className="self-stretch px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex justify-between items-center">
                            <div className="flex-1 justify-center text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
                              {getRelationTypeLabel(requiredRelationType)}
                            </div>
                          </div>
                          <div className="justify-center text-neutral-400 text-[10px] font-normal font-['Bricolage_Grotesque']">
                            Type de relation défini par le recruteur
                          </div>
                          {/* Champ caché pour maintenir la valeur dans le formulaire */}
                          <input
                            type="hidden"
                            {...form.register(`referents.${index}.relation_type`)}
                            value={requiredRelationType}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Séparateur entre les références (sauf pour la dernière) */}
                  {index < referents.length - 1 && (
                    <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-neutral-400/20"></div>
                  )}
                </div>
              );
            })}

            {/* Séparateur avant le bouton */}
            <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-neutral-400/20"></div>

            {/* Erreurs de soumission */}
            {submitError && (
              <div className="w-full bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 font-medium">Erreur de soumission</p>
                <p className="text-red-600">{submitError}</p>
                {Object.keys(validationErrors).length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm text-red-600">Erreurs de validation :</p>
                    <ul className="list-disc list-inside text-sm text-red-600">
                      {Object.entries(validationErrors).map(([key, error]) => (
                        <li key={key}>Référent {parseInt(key) + 1}: {error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Consentement fingerprinting */}
            <FingerprintConsentCheckbox
              checked={fingerprintConsent}
              onCheckedChange={setFingerprintConsent}
              disabled={isSubmitting || isValidating}
              className="w-full"
            />

            {/* Erreur fingerprinting */}
            {fingerprintError && (
              <div className="w-full bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-yellow-800 text-sm">
                  ⚠️ Fingerprinting non disponible: {fingerprintError}
                </p>
                <p className="text-yellow-700 text-xs mt-1">
                  Vous pouvez continuer sans cette fonctionnalité.
                </p>
              </div>
            )}

            {/* Statut fingerprinting */}
            {fingerprintConsent && isGenerating && (
              <BrandedBackground
                branding={branding}
                opacity={0.1}
                withBorder={true}
                className="w-full rounded-lg p-3"
              >
                <div className="flex items-center gap-2">
                  <BrandedIcon 
                    Icon={Loader2} 
                    branding={branding}
                    className="h-4 w-4 animate-spin"
                  />
                  <BrandedText 
                    className="text-sm"
                    branding={branding}
                    as="p"
                  >
                    Génération de l'empreinte device...
                  </BrandedText>
                </div>
              </BrandedBackground>
            )}

            {/* Bouton de soumission */}
            <button
              type="submit"
              disabled={isSubmitting || isValidating}
              className="w-full h-12 p-3 rounded-[10px] inline-flex justify-center items-center gap-1 overflow-hidden hover:opacity-90 disabled:opacity-50"
              style={{
                backgroundColor: getBrandedValue(branding?.primaryColor, '#2dd4bf')
              }}
            >
              <div className="justify-center text-white text-sm sm:text-base font-semibold font-['Bricolage_Grotesque']">
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Envoi en cours...
                  </>
                ) : isValidating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Validation...
                  </>
                ) : (
                  'Soumettre les référents'
                )}
              </div>
            </button>
          </form>

          {/* Séparateur final */}
          <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-neutral-400/20"></div>

          {/* Footer */}
          <div className="flex flex-col justify-center items-center gap-4">
            <div className="w-full max-w-96 text-center justify-center text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque'] leading-none">
              Cette demande de référence est effectuée conformément à la législation en vigueur. Vos réponses sont confidentielles et traitées conformément au RGPD.
            </div>
            <div
              className="text-center justify-center text-sm font-normal font-['Bricolage_Grotesque'] leading-tight"
              style={{ color: getBrandedValue(branding?.primaryColor, '#2dd4bf') }}
            >
              L'équipe Getproof
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
